import { 
  Target, 
  Stethoscope,
  Heart,
  Shield,
  Lock,
  Database,
  Zap,
  CheckCircle,
  FileText,
  Award,
} from "lucide-react";
import FeaturesShowcase from "./FeaturesShowcase";
import { Card, CardContent, CardHeader, CardTitle } from "@/ui";

// Brand colors
const PRIMARY_BLUE = "#1356a6";
const HERO_BLUE = "#0d3b6e";

const platformImage = "/assets/website/images/Healthcare_AI_platform_interface_d8311b6d.png";

const platformKPIs = [
  { metric: "60%", label: "Faster Check-In", icon: Target },
  { metric: "90%", label: "Patient Satisfaction", icon: Heart },
  { metric: "45%", label: "Reduced Wait Times", icon: Stethoscope },
  { metric: "99.9%", label: "Security Uptime", icon: Shield }
];

const securityFeatures = [
  {
    icon: Shield,
    title: "HIPAA Compliance",
    description: "End-to-end encryption and privacy controls",
    details: ["256-bit encryption", "Access logging", "Audit trails", "BAA agreements"]
  },
  {
    icon: Lock,
    title: "Multi-Tenant Isolation",
    description: "Complete data separation between organizations",
    details: ["Tenant-scoped access", "Isolated storage", "Network segmentation", "Identity management"]
  },
  {
    icon: Database,
    title: "Privacy-First Storage",
    description: "Minimal data retention with automatic purging",
    details: ["Data minimization", "Automated purging", "Anonymization", "Consent management"]
  },
  {
    icon: Zap,
    title: "Real-Time Monitoring",
    description: "24/7 security monitoring and threat detection",
    details: ["Intrusion detection", "Anomaly monitoring", "Incident response", "SOC2 compliant"]
  }
];

const complianceStandards = [
  { name: "HIPAA", description: "Health Insurance Portability and Accountability Act" },
  { name: "SOC2 Type II", description: "Security, Availability, and Confidentiality" },
  { name: "ISO 27001", description: "Information Security Management" },
  { name: "HITRUST", description: "Health Information Trust Alliance" }
];

export default function UrgentCarePlatform() {

  return (
    <section id="urgent-care-platform" className="py-20">
      {/* Hero Image Section */}
      <div className="relative h-80 mb-20 overflow-hidden">
        <img 
          src={platformImage} 
          alt="Urgent Care AI Platform Interface"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/80 to-background/60" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-normal mb-4" style={{ color: PRIMARY_BLUE }} data-testid="text-urgent-care-title">
              Streamlined Urgent Care Operations
            </h2>
            <p className="text-2xl sm:text-3xl max-w-4xl mx-auto leading-tight" style={{ color: PRIMARY_BLUE, lineHeight: "1.3" }} data-testid="text-urgent-care-description">
              Transform your urgent care facility with AI-powered workflows that enhance patient experience,{" "}
              <br />
              streamline operations, and ensure optimal care delivery.
            </p>
          </div>
        </div>
      </div>
      
      <div className="w-full px-4 py-16">

        {/* Platform Capabilities */}
        <div className="mb-32">
          <FeaturesShowcase />
        </div>

        {/* Platform Impact - styled like Measurable Outcomes */}
        <div className="mb-20">
          <div
            className="rounded-2xl backdrop-blur-md border border-white/30 shadow-xl overflow-hidden p-6 lg:p-8"
            style={{
              background: `linear-gradient(135deg, ${PRIMARY_BLUE} 0%, ${HERO_BLUE} 100%)`
            }}
          >
            {/* Section header - white text */}
            <div className="mb-8 animate-fade-in-up">
              <div className="text-center">
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-normal mb-4 text-white">
                  Platform Impact<sup className="text-white/70 text-xs">*</sup>
                </h2>
                <p className="text-lg max-w-2xl mx-auto text-white">
                  Measurable improvements in primary<span className="text-white/70 text-xs">*</span> and urgent<span className="text-white/70 text-xs">*</span> care operations and patient outcomes
                </p>
              </div>
            </div>

            {/* Stats grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
              {platformKPIs.map((kpi, index) => {
                const Icon = kpi.icon;
                return (
                  <div
                    key={index}
                    className="group relative rounded-xl backdrop-blur-md border border-white/20 shadow-lg p-6 lg:p-8 text-center transition-all duration-300 hover:-translate-y-1 animate-fade-in-up bg-white/10"
                    style={{ 
                      animationDelay: `${index * 100}ms`,
                    }}
                  >
                    {/* Icon */}
                    <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-white/10 flex items-center justify-center">
                      <Icon className="w-6 h-6 text-white/70" />
                    </div>

                    {/* Value */}
                    <div className="text-3xl sm:text-4xl lg:text-5xl font-normal mb-2 text-white">
                      {kpi.metric}
                    </div>

                    {/* Label */}
                    <div className="text-sm sm:text-base text-white font-medium">{kpi.label}</div>
                  </div>
                );
              })}
            </div>

            {/* Disclaimer */}
            <p className="text-center text-white/50 text-sm mt-8">
              *Early pilot estimates; actual results may vary.
            </p>
          </div>
        </div>

        {/* Security & Privacy Features */}
        <div className="mb-20">
          <div
            className="rounded-2xl backdrop-blur-md border border-white/30 shadow-xl overflow-hidden p-6 lg:p-8"
            style={{
              background: `linear-gradient(135deg, ${PRIMARY_BLUE} 0%, ${HERO_BLUE} 100%)`
            }}
          >
            {/* Section header - white text */}
            <div className="mb-8 animate-fade-in-up">
              <div className="text-center">
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-normal mb-4 text-white">
                  Security & Privacy Features
                </h2>
                <p className="text-lg max-w-2xl mx-auto text-white">
                  Enterprise-grade security and compliance built into every layer of our platform
                </p>
              </div>
            </div>

            {/* Security Features Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
              {securityFeatures.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div
                    key={index}
                    className="group relative rounded-xl backdrop-blur-md border border-white/20 shadow-lg p-6 text-center transition-all duration-300 hover:-translate-y-1 animate-fade-in-up bg-white/10"
                    style={{ 
                      animationDelay: `${index * 100}ms`,
                    }}
                  >
                    {/* Icon */}
                    <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-white/10 flex items-center justify-center">
                      <Icon className="w-12 h-12 text-white/70" />
                    </div>

                    {/* Title */}
                    <h3 className="text-2xl font-semibold mb-4 text-white">
                      {feature.title}
                    </h3>

                    {/* Description */}
                    <p className="text-base text-white/80 mb-6">
                      {feature.description}
                    </p>

                    {/* Details */}
                    <ul className="space-y-3 text-lg text-white text-left">
                      {feature.details.map((detail, detailIndex) => (
                        <li key={detailIndex} className="flex items-start gap-2">
                          <CheckCircle className="h-6 w-6 mt-0.5 flex-shrink-0 text-white/60" />
                          <span>{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Compliance Standards */}
        <div className="mb-20">
          <div
            className="rounded-2xl backdrop-blur-md border border-white/30 shadow-xl overflow-hidden p-6 lg:p-8"
            style={{
              background: `linear-gradient(135deg, ${PRIMARY_BLUE} 0%, ${HERO_BLUE} 100%)`
            }}
          >
            {/* Section header - white text */}
            <div className="mb-8 animate-fade-in-up">
              <div className="text-center">
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-normal mb-4 text-white">
                  Designing for Compliance & Certifications
                </h2>
                <p className="text-lg max-w-2xl mx-auto text-white">
                  Built with healthcare compliance standards at the foundation
                </p>
              </div>
            </div>

            {/* Compliance Standards Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
              {complianceStandards.map((standard, index) => (
                <div
                  key={index}
                  className="group relative rounded-xl backdrop-blur-md border border-white/20 shadow-lg p-6 text-center transition-all duration-300 hover:-translate-y-1 animate-fade-in-up bg-white/10"
                  style={{ 
                    animationDelay: `${index * 100}ms`,
                  }}
                >
                  {/* Icon */}
                  <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-white/10 flex items-center justify-center">
                    <Award className="w-12 h-12 text-white/70" />
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl font-semibold mb-4 text-white">
                    {standard.name}
                  </h3>

                  {/* Description */}
                  <p className="text-lg text-white">
                    {standard.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
